import React, { useState, useEffect } from 'react';
import axios from '../utils/axios';
import { toast } from 'react-toastify';
import Navbar from '../components/Navbar';
import {
  Users, Calendar, TrendingUp, Award, Clock, CheckCircle,
  Activity, BarChart3, Zap, Target, ArrowUpRight, TrendingDown
} from 'lucide-react';
import {
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, PieChart, Pie, Cell
} from 'recharts';
import { DashboardStats, Member } from '../types';

const AdminDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats | null>(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get('/dashboard/admin');
      setStats(response.data.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
      console.error('Dashboard data error:', error);
    } finally {
      setLoading(false);
    }
  };

  const departmentData = [
    { name: 'Engineering', value: 340, color: '#0D5128' },
    { name: 'Business', value: 280, color: '#C49A3C' },
    { name: 'Sciences', value: 210, color: '#1a7a4a' },
    { name: 'Arts', value: 150, color: '#a97f2f' },
  ];

  const growthData = stats?.monthlyTrend?.map(item => ({
    month: new Date(item.month).toLocaleDateString('en-US', { month: 'short' }),
    attendance: parseInt(item.attendance_count),
    events: Math.floor(parseInt(item.attendance_count) / 15),
  })) || [];

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-secondary-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0D5128] mx-auto mb-4"></div>
            <p className="text-secondary-500 text-sm">Loading dashboard...</p>
          </div>
        </div>
      </>
    );
  }

  const totalMembers = parseInt(stats?.memberStats?.total_members || '0');
  const approvedMembers = parseInt(stats?.memberStats?.approved_members || '0');
  const pendingMembers = parseInt(stats?.memberStats?.pending_members || '0');
  const totalEvents = parseInt(stats?.eventStats?.total_events || '0');
  const upcomingEvents = parseInt(stats?.eventStats?.upcoming_events || '0');
  const totalAttendance = parseInt(stats?.attendanceStats?.total_attendance || '0');

  const approvalRate = totalMembers > 0 ? ((approvedMembers / totalMembers) * 100).toFixed(1) : '0';
  const attendanceRate = totalEvents > 0 ? ((totalAttendance / (totalEvents * approvedMembers || 1)) * 100).toFixed(1) : '0';
  const avgPoints = approvedMembers > 0
    ? Math.round(parseInt(stats?.attendanceStats?.total_points_distributed || '0') / approvedMembers)
    : 0;

  const statCards = [
    {
      label: 'Total Members',
      value: totalMembers.toLocaleString(),
      sub: `${approvalRate}% approval rate`,
      icon: Users,
      iconBg: '#0D5128',
      trend: '+12%',
      trendUp: true,
    },
    {
      label: 'Approved Members',
      value: approvedMembers.toLocaleString(),
      sub: `${pendingMembers} pending review`,
      icon: CheckCircle,
      iconBg: '#1a7a4a',
      trend: `${approvalRate}%`,
      trendUp: true,
    },
    {
      label: 'Pending Review',
      value: pendingMembers.toLocaleString(),
      sub: 'Awaiting approval',
      icon: Clock,
      iconBg: '#C49A3C',
      trend: null,
      trendUp: false,
    },
    {
      label: 'Total Events',
      value: totalEvents.toLocaleString(),
      sub: `${upcomingEvents} upcoming`,
      icon: Calendar,
      iconBg: '#a97f2f',
      trend: `${upcomingEvents} soon`,
      trendUp: true,
    },
  ];

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-secondary-50 pb-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">

          {/* Page Header */}
          <div className="mb-6 sm:mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-secondary-900 mb-1">Dashboard</h1>
                <p className="text-sm text-secondary-500">
                  Hammad Foundation Volunteer Network — {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
              </div>
              <div className="hidden sm:flex items-center gap-2 bg-white border border-[#0D5128]/20 rounded-xl px-4 py-2 shadow-sm">
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
                <span className="text-xs font-medium text-[#0D5128]">Live</span>
              </div>
            </div>
          </div>

          {/* Primary Stats Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {statCards.map((card, i) => {
              const Icon = card.icon;
              return (
                <div
                  key={i}
                  className="bg-white rounded-xl shadow-sm border border-secondary-200 p-5 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: card.iconBg }}
                    >
                      <Icon size={20} className="text-white" />
                    </div>
                    {card.trend && (
                      <div className={`flex items-center gap-1 text-xs font-medium ${card.trendUp ? 'text-emerald-600' : 'text-amber-600'}`}>
                        <ArrowUpRight size={12} />
                        {card.trend}
                      </div>
                    )}
                    {card.label === 'Pending Review' && pendingMembers > 0 && (
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-100 text-amber-700 text-xs font-bold">
                        {pendingMembers}
                      </span>
                    )}
                  </div>
                  <div className="text-2xl sm:text-3xl font-bold text-secondary-900 mb-1">{card.value}</div>
                  <div className="text-xs font-semibold text-secondary-500 uppercase tracking-wider mb-0.5">{card.label}</div>
                  <div className="text-xs text-secondary-400">{card.sub}</div>
                </div>
              );
            })}
          </div>

          {/* Secondary Stats Row */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-xl shadow-sm border border-secondary-200 p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#0D5128]/10">
                <Activity size={20} className="text-[#0D5128]" />
              </div>
              <div>
                <div className="text-xs text-secondary-500 uppercase tracking-wider mb-0.5">Attendance Rate</div>
                <div className="text-xl font-bold text-secondary-900">{attendanceRate}%</div>
              </div>
              <div className="ml-auto flex items-center gap-1 text-emerald-600 text-xs font-medium">
                <ArrowUpRight size={12} />5.2%
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-secondary-200 p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#C49A3C]/10">
                <Zap size={20} className="text-[#C49A3C]" />
              </div>
              <div>
                <div className="text-xs text-secondary-500 uppercase tracking-wider mb-0.5">Total Attendance</div>
                <div className="text-xl font-bold text-secondary-900">{totalAttendance.toLocaleString()}</div>
              </div>
              <div className="ml-auto flex items-center gap-1 text-emerald-600 text-xs font-medium">
                <ArrowUpRight size={12} />8.4%
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-secondary-200 p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#0D5128]/10">
                <Target size={20} className="text-[#0D5128]" />
              </div>
              <div>
                <div className="text-xs text-secondary-500 uppercase tracking-wider mb-0.5">Avg Points / Member</div>
                <div className="text-xl font-bold text-secondary-900">{avgPoints}</div>
              </div>
              <div className="ml-auto flex items-center gap-1 text-rose-500 text-xs font-medium">
                <TrendingDown size={12} />2.1%
              </div>
            </div>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
            {/* Area Chart */}
            <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-secondary-200 p-5">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h3 className="text-base font-bold text-secondary-900">Growth Trajectory</h3>
                  <p className="text-xs text-secondary-400 mt-0.5">Monthly attendance & event trend</p>
                </div>
                <BarChart3 size={18} className="text-[#0D5128]" />
              </div>
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={growthData}>
                  <defs>
                    <linearGradient id="colorAttendance" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0D5128" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#0D5128" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorEvents" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#C49A3C" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#C49A3C" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis
                    dataKey="month"
                    stroke="#cbd5e1"
                    tick={{ fill: '#94a3b8', fontSize: 11 }}
                  />
                  <YAxis
                    stroke="#cbd5e1"
                    tick={{ fill: '#94a3b8', fontSize: 11 }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#ffffff',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '12px',
                      boxShadow: '0 4px 6px -1px rgba(0,0,0,0.07)',
                    }}
                  />
                  <Area type="monotone" dataKey="attendance" stroke="#0D5128" strokeWidth={2} fillOpacity={1} fill="url(#colorAttendance)" name="Attendance" />
                  <Area type="monotone" dataKey="events" stroke="#C49A3C" strokeWidth={2} fillOpacity={1} fill="url(#colorEvents)" name="Events" />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Pie Chart */}
            <div className="bg-white rounded-xl shadow-sm border border-secondary-200 p-5">
              <div className="mb-5">
                <h3 className="text-base font-bold text-secondary-900">Distribution</h3>
                <p className="text-xs text-secondary-400 mt-0.5">Members by department</p>
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={departmentData}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={85}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    {departmentData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#ffffff',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '12px',
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {departmentData.map((dept, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: dept.color }}></div>
                    <span className="text-xs text-secondary-500 truncate">{dept.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Bottom Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Recent Members */}
            <div className="bg-white rounded-xl shadow-sm border border-secondary-200 p-5">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-base font-bold text-secondary-900">Recent Registrations</h3>
                <Users size={18} className="text-[#0D5128]" />
              </div>
              <div className="space-y-3 max-h-72 overflow-y-auto">
                {stats?.recentMembers && stats.recentMembers.length > 0 ? (
                  stats.recentMembers.map((member: Member, idx: number) => (
                    <div key={member.id} className="flex items-center gap-3 p-3 rounded-lg bg-secondary-50 hover:bg-secondary-100 transition-colors">
                      <div
                        className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                        style={{ backgroundColor: ['#0D5128', '#C49A3C', '#1a7a4a', '#a97f2f'][idx % 4] }}
                      >
                        {member.full_name.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-secondary-900 truncate">{member.full_name}</p>
                        <p className="text-xs text-secondary-400 truncate">{member.member_id} · {member.department}</p>
                      </div>
                      <span className={`text-[10px] font-semibold px-2 py-1 rounded-full ${
                        member.status === 'approved'
                          ? 'bg-emerald-100 text-emerald-700'
                          : member.status === 'pending'
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {member.status}
                      </span>
                    </div>
                  ))
                ) : (
                  <p className="text-center py-10 text-secondary-400 text-sm">No recent registrations</p>
                )}
              </div>
            </div>

            {/* Top Performers */}
            <div className="bg-white rounded-xl shadow-sm border border-secondary-200 p-5">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-base font-bold text-secondary-900 flex items-center gap-2">
                  <Award size={18} className="text-[#C49A3C]" />
                  Top Performers
                </h3>
                <TrendingUp size={18} className="text-emerald-500" />
              </div>
              <div className="space-y-3 max-h-72 overflow-y-auto">
                {stats?.topPerformers && stats.topPerformers.length > 0 ? (
                  stats.topPerformers.map((member: Member, index: number) => (
                    <div key={member.id} className="flex items-center gap-3 p-3 rounded-lg bg-secondary-50 hover:bg-secondary-100 transition-colors">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                        index === 0 ? 'bg-[#C49A3C] text-white' :
                        index === 1 ? 'bg-secondary-300 text-secondary-700' :
                        index === 2 ? 'bg-amber-700/20 text-amber-800' :
                        'bg-secondary-100 text-secondary-500'
                      }`}>
                        #{index + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-secondary-900 truncate">{member.full_name}</p>
                        <p className="text-xs text-secondary-400 truncate">{member.department}</p>
                      </div>
                      <div className="text-sm font-bold text-[#0D5128] whitespace-nowrap">
                        {member.total_points} <span className="text-xs font-normal text-secondary-400">pts</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-center py-10 text-secondary-400 text-sm">No performance data</p>
                )}
              </div>
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default AdminDashboard;
